import React, { useEffect, useMemo, useState } from 'react'
import { ArrowLeftOutlined, UploadOutlined, SaveOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { BackButton, Description, EmptyUpload, ExampleWrapper, InfoWrapper, SubTitle, TableHeader, UploadWrapper } from './location.styled'
import { Button, ConfigProvider, Input, InputNumber, message, Radio, Select, Spin, Upload } from 'antd'
import UploadExample from './components/UploadExample'
import { readCsvToJsonLocation } from '../../components/utils/readExcel'
import useParams from '../../hook/useParams'
import MainTable from '../../components/Basic/MainTable'
import { useSelector } from 'react-redux'
import { bulkAddLocation, bulkAddLocationTags, fetchLocationTags } from '../../apis/location'

const columns = {
  geo: ['Name', 'Tags', 'Latitude', 'Longitude', 'Radius'],
  address: ['Name', 'Tags', 'Address', 'Radius']
}
const acceptedUploadFormat = '.csv, text/csv'
const maxUpload = 100 // maximum upload at a time
const numberInput = ['Radius']
const requiredFields = ['name', 'address', 'latitude', 'longitude', 'radius']

function LocationUpload (props) {
  const { goBackPath } = props
  const { t: ln_t } = useTranslation()
  const navigate = useNavigate()
  const user = useSelector((state) => state.user)
  const { nowBrand, id } = user || {}
  const [type, setType] = useState('address')
  const [tagList, setTagList] = useState([])
  const [fileList, setFileList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)

  const menu = [{ value: 'address', label: ln_t('location.address') }, { value: 'geo', label: ln_t('location.latLong') }]
  const goBack = () => navigate(goBackPath)

  const anyEmpty = (data, requiredFields) => data.some(i => requiredFields?.some(col => (!i[col] && i[col] === '')))
  const handleSave = async () => {
    const { data, uploadType } = fetchParams || {}
    if (!data || data.length === 0) return message.error(ln_t('message.error.noUploadData'))
    const requiredField = columns[uploadType]?.filter(i => requiredFields.includes(i.toLowerCase()))
    if (anyEmpty(data, requiredField?.map(i => i.toLowerCase()))) return message.error(`${requiredField.join(', ')} ${ln_t('message.error.areRequiredFields')}`)

    setSubmitLoading(true)
    const allTags = data?.map(i => i?.tags?.split('@') || [])?.flat() || []
    const newTags = [...new Set(allTags)].filter(i => !tagList.includes(i))
    if (newTags.length) {
      const addTag = await bulkAddLocationTags({ locationTags: newTags.map(i => ({ name: i, brandId: nowBrand })) })
      if (addTag?.status !== 200) return
    }
    const formatData = data.map(({ id, ...other }) => other)
    const res = await bulkAddLocation({ locations: formatData })
    if (res?.status === 200) {
      message.success(ln_t('message.success.upload'))
      goBack()
    }
    setSubmitLoading(false)
  }

  // ----- upload config -----
  const handleUpload = (file) => {
    setIsLoading(true)
    readCsvToJsonLocation(file, columns[type], maxUpload).then(({ status, message: msg = 'message.error.uploadError', data }) => {
      if (!status) return message.error(ln_t(msg))
      const formattedData = data.map((item, index) => ({
        ...item,
        id: index,
        locationType: type,
        radius: !item.radius ? 25 : Number(item.radius) < 25 ? 25 : Number(item.radius) > 1000 ? 1000 : Math.round(Number(item.radius)),
        tags: item?.tags?.replace(/＠/gi, '@'),
        geoTranslate: type === 'address' ? 0 : 2,
        brandId: nowBrand,
        creatorId: id,
        updaterId: id
      }))
      handleFetchParams('add', { data: formattedData, uploadType: type })
      setFileList([file])
    })
    setIsLoading(false)
    return false
  }

  const uploadProps = {
    accept: acceptedUploadFormat,
    maxCount: 1,
    fileList,
    beforeUpload: file => handleUpload(file),
    onRemove: () => setFileList([]),
    showUploadList: { showRemoveIcon: false }
  }

  // ----- config of table after upload -----
  const apiTable = async () => ({ status: 200, data: fetchParams?.data || [] })
  const params = useParams()
  const { handleFetchParams, fetchParams } = params
  const columnsByType = useMemo(() => {
    const { data, uploadType } = fetchParams || {}
    if (!data || data.length === 0) return columns[uploadType || type]?.map(i => ({ title: i, dataIndex: i }))
    return columns[uploadType]?.map(item => ({
      title: item,
      dataIndex: item.toLowerCase(),
      render: (e, record) => {
        const onChange = value => {
          const n = [...data]
          const index = n.indexOf(record)
          if (index !== -1) {
            n[index] = { ...n[index], [item.toLowerCase()]: item !== 'Tags' ? value : value.join('@') }
            handleFetchParams('add', { data: n })
          }
        }
        const numInputProps = { min: 25, max: 1000 }
        const props = { style: { width: '100%' } }

        if (numberInput.includes(item)) return <InputNumber value={Number(e) || 0} {...props} {...numInputProps} onChange={e => onChange(e)} />
        if (item === 'Tags') return <Select mode='tags' options={tagList.map(i => ({ value: i, label: i }))} value={e ? e.split('@') : []} {...props} onChange={e => onChange(e)} />
        return <Input value={e || ''} {...props} onChange={e => onChange(e.target.value)} />
      }
    }))
  }, [fetchParams])

  const getLocationTag = async () => {
    const res = await fetchLocationTags({ maxResults: 999, brandId: nowBrand, status: 1 })
    if (res?.status) setTagList(res?.data?.map(i => i.name))
  }
  useEffect(() => { getLocationTag() }, [])

  return (
    <>
      <BackButton>
        <ArrowLeftOutlined onClick={() => goBack()} />
        {ln_t('location.upload')} {ln_t('sidebar.location')}
      </BackButton>
      <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
        <UploadWrapper>
          <ExampleWrapper>
            <SubTitle section>{ln_t('location.addressFileExample')}</SubTitle>
            <UploadExample type="address" columns={columns} />
            <SubTitle section style={{ marginTop: '32px' }}>{ln_t('location.latLongFileExample')}</SubTitle>
            <UploadExample type="geo" columns={columns} />
          </ExampleWrapper>
          <InfoWrapper>
            <div className="option-and-btn">
              <Radio.Group value={type} onChange={e => setType(e.target.value)} options={menu} optionType="button" />
              <Upload {...uploadProps}>
                <Button type="primary" icon={<UploadOutlined />}>{ln_t('location.upload')}</Button>
              </Upload>
            </div>
            <Description>
              <div className="title">{ln_t('location.fileFormatDescription')}</div>
              <ul>
                <li>{ln_t('location.rule.supportedFormat')}</li>
                <li>{ln_t('location.rule.maxUpload')}: {maxUpload} {ln_t('location.rule.atATime')}</li>
                <li>{ln_t('location.rule.includeHeaders')}</li>
                <li>{ln_t('location.rule.inputCompleteAddress')}</li>
                <li>{ln_t('location.rule.separateTags')}</li>
                <li>{ln_t('location.rule.radius')}</li>
                <li>{ln_t('location.rule.noDuplicate')}</li>
              </ul>
            </Description>
          </InfoWrapper>
        </UploadWrapper>
        {fileList && fileList?.length > 0
          ? isLoading
            ? <Spin style={{ display: 'flex', justifyContent: 'center', margin: '60px 0px' }} />
            : <>
              <TableHeader>
                <SubTitle style={{ padding: '0px', margin: 'auto 0px' }}>{ln_t('location.checkAndConfirm')}</SubTitle>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={submitLoading}>{ln_t('location.saveThisData')}</Button>
              </TableHeader>
              <MainTable
                columns={columnsByType}
                rowKey="id"
                apiFunc={apiTable}
                params={params}
              />
            </>
          : <EmptyUpload>{ln_t('location.pleaseUploadFirst')}</EmptyUpload>
        }
      </ConfigProvider>
    </>
  )
}

export default LocationUpload
